import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Host,
  Input,
  Optional, Output,
  Self,
  SkipSelf,
  ViewChild
} from '@angular/core';
import { ControlContainer, FormBuilder, FormControl, NgControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import { InputBase } from 'mbs-ui-kit';
import { SelectComponent } from 'mbs-ui-kit/form/select/select.component';
import { BehaviorSubject, Observable, Subject, combineLatest, merge, of } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { PermissionsEnum } from 'mbs-ui-shared';

@UntilDestroy()
@Component({
  selector: 'mbs-paged-select',
  templateUrl: './paged-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PagedSelectComponent<T> extends InputBase<T | T[]> implements AfterViewInit {
  public readonly validators = Validators;
  public readonly permissionsEnum = PermissionsEnum;

  protected bindLabel = 'name';
  protected loading = false;

  private proposedValues: T | T[];

  private selectedIds$ = new BehaviorSubject<string[]>(null);
  private fetchData = new Subject<void>();

  public items: T[] = [];
  public control: FormControl<T | T[]>;
  public typeahead$: Subject<string> = new Subject<string>();

  @Input() multiple = false;
  @Input() counterLabel: string = null;
  @Input() formControlName?: string;

  @Input() set selectedId(value: string) {
    this.selectedIds$.next(value ? [value] : []);
  }
  @Input() set selectedIds(values: string[]) {
    this.selectedIds$.next(values);
  }
  @Input() appendTo?: string;
  @Input() clearable = true;
  @Input() searchable = true;
  @Input() showAddNewButton?: boolean;

  @Output() closeEvent: EventEmitter<void> = new EventEmitter();
  @Output() clearEvent: EventEmitter<void> = new EventEmitter();

  @ViewChild(SelectComponent, { static: false }) pagedSelect: SelectComponent;

  constructor(
    @Optional() @Host() @SkipSelf() public controlContainer: ControlContainer,
    @Optional() @Self() protected ngControl: NgControl,
    protected fb: FormBuilder,
    protected cdr: ChangeDetectorRef
  ) {
    super(ngControl, cdr);
  }

  ngOnInit() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName) as FormControl;
      } else {
        console.warn('Missing formControlName directive');
      }
    } else {
      this.control = this.ngControl.control as FormControl;
    }
  }

  ngAfterViewInit(): void {
    this.initStreams();
  }

  writeValue(val: T | T[]) {
    if (
      isEqual(this.proposedValues, val) ||
      (this.multiple && !(this.proposedValues as T[])?.length && !(val as T[])?.length) ||
      (!this.multiple && !this.proposedValues && !val)
    )
      return;

    this.proposedValues = val;
    queueMicrotask(() => (this.value = this.proposedValues));
  }

  protected loadData(): void {
    //
  }

  protected get getData$(): Observable<T[]> {
    return of([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadById(id: string): void {
    //
  }

  protected get getTotal$(): Observable<number> {
    return of(0);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getById$(id: string): Observable<T> {
    return of(null);
  }

  protected get allLoaded$(): Observable<boolean> {
    return of(false);
  }

  protected get dataHasError$(): Observable<boolean> {
    return of(false);
  }

  protected get loadingData$(): Observable<boolean> {
    return of(false);
  }

  protected get loadingById$(): Observable<boolean> {
    return of(false);
  }

  protected areItemsTheSame(selectedItem: T, item: T): boolean {
    return false;
  }

  public close() {
    this.pagedSelect?.ngSelect.close();
  }

  private initStreams() {
    combineLatest([this.loadingData$, this.loadingById$])
      .pipe(map(([loadingData, loadingById]) => loadingData || loadingById))
      .pipe(untilDestroyed(this))
      .subscribe((loading) => {
        queueMicrotask(() => (this.loading = loading));
      });

    const selectScrollToEnd$ = this.pagedSelect.scrollToEnd;
    const selectScroll$ = this.pagedSelect.scroll.pipe(filter(({ end }) => end + 10 >= this.items.length));

    merge(selectScrollToEnd$, selectScroll$)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.fetchData.next());

    const loadAllowed$ = this.loadingData$.pipe(
      filter((loading) => !loading),
      take(1)
    );

    this.fetchData
      .pipe(
        withLatestFrom(this.allLoaded$, this.dataHasError$),
        filter(([, loaded, hasError]) => !loaded && !hasError),
        switchMap(() => loadAllowed$),
        untilDestroyed(this)
      )
      .subscribe(() => this.loadData());

    this.selectedIds$
      .pipe(
        filter((selectedIds) => !!selectedIds?.length && this.items.length === 0),
        untilDestroyed(this)
      )
      .subscribe((selectedIds) => {
        selectedIds.forEach((selectedId) => this.loadById(selectedId));
        this.fetchData.next();
      });

    const getByIdWhenLoaded$ = (id: string) => this.getById$(id).pipe(filter(Boolean), take(1));

    const loadedSelectedItems$ = this.selectedIds$.pipe(
      switchMap((selectedIds) =>
        selectedIds?.length ? combineLatest(selectedIds.map((selectedId) => getByIdWhenLoaded$(selectedId))) : of(null)
      )
    );

    loadedSelectedItems$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((selectedItems) => {
      queueMicrotask(() => this.control.patchValue(this.multiple ? selectedItems : selectedItems[0]));
    });

    combineLatest([this.getData$, loadedSelectedItems$])
      .pipe(
        map(([items, selectedItems]) => {
          return selectedItems?.length
            ? (selectedItems as T[]).concat(
                items.filter((item) => !selectedItems.some((selectedItem) => this.areItemsTheSame(selectedItem, item)))
              )
            : items;
        }),
        untilDestroyed(this)
      )
      .subscribe((items) => {
        queueMicrotask(() => {
          this.items = items;
          this.cdr.markForCheck();
        });
      });
  }
}
